import React, { useContext, useState } from "react";
import { formatClockText } from '../../../../../platform/util/clockSystem';
import EthiopianTimeField from '../../../../../platform/util/EthiopianTimeField';
import CalendarDateField from '../../../../../platform/util/CalendarDateField';
import { quickCalendarOccurrences } from '../../../../../platform/util/quickCalendarOccurrences';
import BookingTypeNotes from "../bookingType/bookingTypeNotes";
import { useMutation } from "@apollo/client";
import {
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  DialogContent,
  DialogActions,
  IconButton,
  Dialog,
  Box,
  Stack,
  Menu,
  TextField,
  Alert,
  Link,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CampaignIcon from "@mui/icons-material/Campaign";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { convertDateTimeToUserTimezone } from "../../../../../platform/util/workingHour";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { StyledDialog } from "../../../../../platform/assets/css/styledComponent";
import AppointmentBooking from "../../../../../platform/pages/business/appointmentBooking";
import CancelSchedule from "../bookingType/cancelSchedule";
import NotifyScheduleChange from "../bookingType/notifyScheduleChange";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import { useCalendar } from "./calendarContext";
import { formatInTimeZone } from 'date-fns-tz';
import { displayBookingDate } from '../../../../../platform/util/bookingCalendarLabel';
import { ADD_QUICK_BOOKING } from "../../../../../platform/graphql/mutations";
import {
  FeatureAccess,
  getFeatureAccess,
} from "../../../../../platform/util/featureAccess";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import { hasStandaloneBookingAccess } from "../../../../../core/business/productAccess";

const looksLikeUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const getBookingLocationDetails = (bookingType) => {
  const rawLocation = String(bookingType?.location || "").trim();
  const meetingLocationTypeId = Number(bookingType?.meetingLocationTypeId || 0);
  const isUrl = looksLikeUrl(rawLocation);
  return {
    rawLocation,
    isUrl,
    meetingLocationTypeId,
    showLocation: !!rawLocation && !isUrl,
    showMeetingLink: !!rawLocation && isUrl,
  };
};

const getBookingNotes = (event) => {
  const notes = String(event?.quickNotes || event?.notes || "").trim();
  return notes || "";
};

const stripHtmlToText = (value) =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\r/g, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

const isGoogleBooking = (booking) =>
  String(booking?.source || booking?.event?.source || "").trim().toLowerCase().includes("google");

const getGoogleBookingLink = (booking) => {
  const rawLink = String(
    booking?.htmlLink ||
    booking?.event?.htmlLink ||
    booking?.hangoutLink ||
    booking?.event?.hangoutLink ||
    ""
  ).trim();
  return looksLikeUrl(rawLink) ? rawLink : "";
};

const BookItOrCreate = ({ triggerDialog }) => {
  const { myBookingTypes, selectedDate, currentMonth, refreshBookings, displayCalendar = 'gregory', displayClock, displayTimezone } = useCalendar();
  const [showCancelModal, setShowCancelModal] = useState(false); // Controls the cancel modal
  const [showNotifyModal, setShowNotifyModal] = useState(false); // Controls the notify modal
  // We'll use selectedBookings consistently for cancellation data.
  const [selectedBookings, setSelectedBookings] = useState([]);
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const selectedBusiness = useSelectedBusiness();
  const isCareBusiness = Boolean(
    selectedBusiness?.careSettings?.afhLicenseNumber ||
    selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber
  );
  const hasStandaloneBooking = hasStandaloneBookingAccess(providerUser, isCareBusiness);

  // Separate state for each dialog
  const [isBookDialogOpen, setIsBookDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);

  // bookingData can be a single event or an array of events (grouped)
  const [bookingData, setBookingData] = useState(null);

  // State for selected options in booking dialog
  const [selectedBooking, setSelectedBooking] = useState("");
  const [selectedBookingDay, setSelectedBookingDay] = useState("");
  const [selectedBookingMonth, setSelectedBookingMonth] = useState("");
  const [quickTitle, setQuickTitle] = useState("");
  const [quickNotes, setQuickNotes] = useState("");
  const [quickInviteeName, setQuickInviteeName] = useState("");
  const [quickInviteeEmail, setQuickInviteeEmail] = useState("");
  const [quickInviteePhones, setQuickInviteePhones] = useState([""]);
  const [quickDate, setQuickDate] = useState("");
  const [quickDateValid, setQuickDateValid] = useState(true);
  const [quickTime, setQuickTime] = useState("");
  const [quickStaffId, setQuickStaffId] = useState("");
  const [quickRecurrence, setQuickRecurrence] = useState("none");
  const [quickOccurrences, setQuickOccurrences] = useState(1);
  const [quickReminder, setQuickReminder] = useState('none');
  const [quickError, setQuickError] = useState("");
  const [addQuickBooking, { loading: quickSaving }] = useMutation(ADD_QUICK_BOOKING);
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);

  const bookingCreateAccess = getFeatureAccess(
    providerUser,
    FeatureAccess.BOOKING_TYPE_CREATE
  );
  const standardPlanRequired = bookingCreateAccess.requiredPlanId;
  const businessId = Number(selectedBusiness?.businessBasic?.id || 0) || null;
  // State for group menu anchor in the grouped update dialog
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);

  // Trigger dialog function (passed via ref from parent)
  const handleOpenDialog = (type, data, day, month) => {
    if (type === "book" && !hasStandaloneBooking) {
      setCheckPlanNeeded(true);
      return;
    }

    setSelectedBookingDay(day || selectedDate || new Date());
    setSelectedBookingMonth(month || currentMonth || new Date());

    if (type === "book") {
      const d = day || selectedDate || new Date();
      const dateObj = d instanceof Date ? d : new Date(d);
      const yyyy = dateObj.getFullYear();
      const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
      const dd = String(dateObj.getDate()).padStart(2, "0");
      setQuickDate(`${yyyy}-${mm}-${dd}`);
      setQuickTime("09:00");
      setQuickRecurrence("none");
      setQuickOccurrences(1);
      setQuickError("");
      setSelectedBooking("");
      setIsBookDialogOpen(true);
    } else if (type === "update") {
      setBookingData(data);
      setIsUpdateDialogOpen(true);
    }
  };

  const handleCloseBookDialog = () => {
    setIsBookDialogOpen(false);
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(false);
    setBookingData(null);
  };

  const cancelModal = () => {
      setShowCancelModal(false);
      setIsUpdateDialogOpen(false);
    // Do not modify bookingData so the update dialog stays unchanged
  };

  const renderScheduleContext = (event, bookingType, includeDescription = false) => {
    if (!event && !bookingType) return null;
    bookingType = (myBookingTypes || []).find(type => Number(type.id) === Number(event?.bookingTypeId || bookingType?.id)) || bookingType;
    const locationDetails = getBookingLocationDetails(bookingType);
    const notes = getBookingNotes(event);
    const description = stripHtmlToText(bookingType?.desc || "");


    return (
      <Stack spacing={1}>
        <Typography variant="body1">
          <strong>Booking Type:</strong> {bookingType?.bookingTypeName || bookingType?.title || event?.bookingTypeName || "N/A"}
        </Typography>
        <Typography variant="body1">
          <strong>Time:</strong>{" "}
          {formatClockText(convertDateTimeToUserTimezone(
            event?.time || event?.startTime,
            displayTimezone
          ) || "N/A", displayClock)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Timezone:</strong> {displayTimezone}
        </Typography>
        {displayCalendar === 'ethiopic' && (event?.time || event?.startTime) && <Typography variant="body2">{displayBookingDate(formatInTimeZone(new Date(event.time || event.startTime), displayTimezone, 'yyyy-MM-dd'), displayCalendar)}</Typography>}
        {Number(bookingType?.meetingLocationTypeId) === 2 && (
          <Alert severity="info">
            {bookingType.canInviteeCall
              ? (bookingType.phoneNumber?.trim()
                ? <>The invitee will call you at <Link href={`tel:${bookingType.phoneNumber.replace(/[^+\d]/g, "")}`}>{bookingType.phoneNumber}</Link>. You do not need to call them.</>
                : "The invitee is expected to call you. No host phone number is saved; contact the invitee to arrange the call.")
              : (event?.inviteePhoneNumbers?.length
                ? `You will call the invitee at ${event.inviteePhoneNumbers.join(", ")}.`
                : "You will call the invitee. Check their contact details before the appointment.")}
          </Alert>
        )}
        {locationDetails.showLocation && (
          <Typography variant="body2" color="text.secondary">
            <strong>Location:</strong> {locationDetails.rawLocation}
          </Typography>
        )}
        {locationDetails.showMeetingLink && (
          <Typography variant="body2" color="text.secondary">
            <strong>Meeting Link:</strong>{" "}
            <Link
              href={locationDetails.rawLocation}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Join meeting
            </Link>
          </Typography>
        )}
        <Typography variant="body1">
          <strong>Provider:</strong> {event?.staffName || "N/A"}
        </Typography>
        {notes && (
          <Typography variant="body2" color="text.secondary">
            <strong>Notes:</strong> {notes}
          </Typography>
        )}
        {!isGoogleBooking(event) && (event?.bookingTypeId || bookingType?.id) && (
          <BookingTypeNotes bookingTypeId={event?.bookingTypeId || bookingType?.id} readOnly />
        )}
        {includeDescription && description && (
          <Typography variant="body2" color="text.secondary">
            <strong>Description:</strong> {description}
          </Typography>
        )}
      </Stack>
    );
  };

  const groupedBookingsAreGoogleOnly = Array.isArray(bookingData)
    ? bookingData.length > 0 && bookingData.every((item) => isGoogleBooking(item?.event || item))
    : false;
  const singleBookingIsGoogle = !Array.isArray(bookingData) && bookingData
    ? isGoogleBooking(bookingData?.event || bookingData)
    : false;
  const groupedGoogleLink = Array.isArray(bookingData)
    ? getGoogleBookingLink(bookingData[0]?.event || bookingData[0])
    : "";
  const singleGoogleLink =
    !Array.isArray(bookingData) && bookingData
      ? getGoogleBookingLink(bookingData?.event || bookingData)
      : "";

  // Expose handleOpenDialog to parent through the triggerDialog prop
  if (triggerDialog) {
    triggerDialog(handleOpenDialog);
  }

  const handleBookingChange = (event) => {
    setSelectedBooking(event.target.value);
  };

  const handleQuickInviteePhoneChange = (index, value) => {
    setQuickInviteePhones((prev) => {
      const next = [...prev];
      next[index] = value;
      return next.length ? next : [""];
    });
  };

  const handleAddQuickInviteePhone = () => {
    setQuickInviteePhones((prev) => [...prev, ""]);
  };

  const handleRemoveQuickInviteePhone = (index) => {
    setQuickInviteePhones((prev) => {
      const next = prev.filter((_, phoneIndex) => phoneIndex !== index);
      return next.length ? next : [""];
    });
  };

  const getNormalizedQuickInviteePhones = () =>
    [...new Set(quickInviteePhones.map((phone) => phone.trim()).filter(Boolean))];

  const handleCreateQuickAppointment = async () => {
    setQuickError("");
    if (!quickTitle.trim()) {
      setQuickError("Title is required.");
      return;
    }
    if (!quickDate || !quickTime || !quickDateValid) {
      setQuickError("Date and time are required.");
      return;
    }

    if (!businessId) {
      setQuickError("No business selected.");
      return;
    }

    const localDateTime = new Date(`${quickDate}T${quickTime}`);
    if (Number.isNaN(localDateTime.getTime())) {
      setQuickError("Invalid date/time.");
      return;
    }
    const totalOccurrences = quickRecurrence === "none" ? 1 : Number(quickOccurrences || 1);
    if (!Number.isFinite(totalOccurrences) || totalOccurrences < 1) {
      setQuickError("Occurrences must be at least 1.");
      return;
    }
    if (totalOccurrences > 52) {
      setQuickError("Maximum 52 occurrences allowed for quick recurring.");
      return;
    }

    try {
      const times = quickCalendarOccurrences(quickDate, quickTime, quickRecurrence, totalOccurrences, displayCalendar, displayTimezone);
      const normalizedInviteePhones = getNormalizedQuickInviteePhones();
      for (const slotTime of times) {
        await addQuickBooking({
          variables: {
            input: {
              businessId,
              staffId: quickStaffId ? Number(quickStaffId) : null,
              timezone: displayTimezone,
              time: slotTime,
              title: quickTitle.trim(),
              reminderMinutes: quickReminder === 'none' ? null : Number(quickReminder),
              calendarSystem: displayCalendar,
              displayClock,
              notes: quickNotes?.trim() || null,
              inviteeName: quickInviteeName?.trim() || null,
              inviteeEmail: quickInviteeEmail?.trim() || null,
              inviteePhone: normalizedInviteePhones[0] || null,
              inviteePhoneNumbers: normalizedInviteePhones,
            },
          },
        });
      }
      await refreshBookings();
      setIsBookDialogOpen(false);
      setSelectedBooking("");
      setQuickTitle("");
      setQuickNotes("");
      setQuickInviteeName("");
      setQuickInviteeEmail("");
      setQuickInviteePhones([""]);
      setQuickStaffId("");
      setQuickRecurrence("none");
      setQuickOccurrences(1);
      setQuickReminder('none');
    } catch (err) {
      setQuickError(err?.message || "Could not create quick appointment.");
    }
  };
 

  // When canceling an individual attendee, set selectedBookings accordingly.
  const handleCancelIndividual = (booking) => {
    setSelectedBookings([booking]);
    setShowCancelModal(true);
  };

  // For group cancellation, use the entire group data.
  const handleCancelGroup = () => {
    setSelectedBookings(bookingData);
    setShowCancelModal(true);
  };

  const handleNotifyIndividual = (booking) => {
    setSelectedBookings([booking]);
    setShowNotifyModal(true);
  };

  const handleNotifyGroup = () => {
    setSelectedBookings(bookingData);
    setShowNotifyModal(true);
  };

  const dialogPaperSx = {
    borderRadius: 3,
    border: 1, borderColor: "divider",
    boxShadow: "0 28px 60px rgba(15, 31, 46, 0.16)",
    overflow: "hidden",
    maxWidth: "1040px",
  };

  const sectionCardSx = {
    border: 1, borderColor: "divider",
    borderRadius: 2.5,
    backgroundColor: "background.paper",
    p: { xs: 1.75, sm: 2.25 },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.8,
        borderRadius: 2.5,
        border: 1, borderColor: "divider",
        boxShadow: "0 20px 40px rgba(15, 31, 46, 0.14)",
        overflow: "hidden",
        bgcolor: "background.paper",
        color: "text.primary",
        "& .MuiMenuItem-root": {
          minHeight: 46,
          fontSize: "0.92rem",
          borderRadius: 1.5,
          margin: "4px 6px",
          "&.Mui-selected": {
            backgroundColor: "rgba(29, 78, 216, 0.08)",
          },
          "&:hover": {
            backgroundColor: "rgba(29, 78, 216, 0.05)",
          },
        },
      },
    },
  };

  return (
    <>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName="Calendar booking"
          requiredPlanId={standardPlanRequired}
          applyExemption={false}
          pricingProduct="booking"
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}
      {/* Booking Dialog */}
      <Dialog
        open={isBookDialogOpen}
        onClose={handleCloseBookDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: dialogPaperSx }}
        sx={{
          "& .MuiDialogContent-root": { overflowY: "auto" },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2.25, sm: 3.25 },
            py: { xs: 2.4, sm: 3 },
            borderBottom: "1px solid rgba(29, 78, 216, 0.10)",
            bgcolor: "background.paper",
            color: "text.primary",
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                  Create or Schedule an Event
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, maxWidth: 560 }}>
                  Choose a booking type or create a quick appointment. This is where you add the event details, timing, staff assignment, and optional attendee info.
                </Typography>
              </Box>
              <IconButton
                aria-label="close"
                onClick={handleCloseBookDialog}
                sx={{
                  mt: -0.5,
                  color: "text.primary",
                  border: 1, borderColor: "divider",
                  backgroundColor: "background.paper",
                  "&:hover": { backgroundColor: "rgba(29,78,216,0.05)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label="Schedule an existing booking type" sx={{ bgcolor: "rgba(29,78,216,0.08)" }} />
              <Chip size="small" label="Or create a quick appointment" sx={{ bgcolor: "rgba(29,78,216,0.08)" }} />
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3.25 }, py: { xs: 2.5, sm: 3.25 } }}>
          <Box sx={{ ...sectionCardSx, mt: 2.1, mb: selectedBooking ? 2.5 : 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Event Type
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Pick an existing booking type if this follows your normal scheduling flow, or use a quick appointment for a one-off calendar event.
            </Typography>
            <FormControl margin="none" fullWidth sx={{ maxWidth: 620 }}>
            <InputLabel id="time-format-label">
              Choose a booking type
            </InputLabel>
            <Select
              value={selectedBooking}
              onChange={handleBookingChange}
              label="Choose a booking type"
              MenuProps={selectMenuProps}
              sx={{
                backgroundColor: "background.paper",
                fontSize: "0.95rem",
                borderRadius: 2.25,
                minHeight: 46,
                boxShadow: "0 10px 22px rgba(15, 31, 46, 0.08)",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  minHeight: "46px !important",
                  py: 1.1,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(29, 78, 216, 0.16)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(29, 78, 216, 0.28)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(29, 78, 216, 0.38)",
                },
                "& .MuiSelect-icon": {
                  color: "text.primary",
                },
              }}
            >
              <MenuItem value="">
                <em>Select Booking Type</em>
              </MenuItem>
              <MenuItem value="__quick__">Quick Appointment (No Booking Type)</MenuItem>
              {myBookingTypes?.map((booking, index) => (
                <MenuItem key={index} value={booking}>
                  {booking.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Box>

          {selectedBooking === "__quick__" && (
            <Box sx={sectionCardSx}>
              <Typography variant="subtitle1" sx={{ mb: 0.75, fontWeight: 700 }}>
                Quick appointment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use this for a fast internal booking or one-off event that does not need a saved booking type.
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Title"
                  value={quickTitle}
                  onChange={(e) => setQuickTitle(e.target.value)}
                  required
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <CalendarDateField label="Date" value={quickDate} onChange={setQuickDate} calendar={displayCalendar} onValidityChange={setQuickDateValid} />
                  {['ethiopian', 'both'].includes(displayClock) ? <EthiopianTimeField showStandard={displayClock === 'both'} label="Time" value={quickTime} onChange={setQuickTime} /> : <TextField
                    size="small"
                    label="Time"
                    type="time"
                    value={quickTime}
                    onChange={(e) => setQuickTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />}
                </Stack>
                <FormControl size="small">
                  <InputLabel id="quick-staff-label">Staff (optional)</InputLabel>
                  <Select
                    labelId="quick-staff-label"
                    value={quickStaffId}
                    label="Staff (optional)"
                    onChange={(e) => setQuickStaffId(e.target.value)}
                  >
                    <MenuItem value=""><em>No staff</em></MenuItem>
                    {(selectedBusiness?.businessStaffs || []).map((staff) => (
                      <MenuItem key={staff.id} value={String(staff.id)}>
                        {staff?.name || staff?.user?.username || `Staff #${staff.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Invitee name (optional)"
                  value={quickInviteeName}
                  onChange={(e) => setQuickInviteeName(e.target.value)}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    size="small"
                    label="Invitee email (optional)"
                    value={quickInviteeEmail}
                    onChange={(e) => setQuickInviteeEmail(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Invitee phones
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={handleAddQuickInviteePhone}
                    >
                      Add phone
                    </Button>
                  </Box>
                  {quickInviteePhones.map((phone, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        label={index === 0 ? "Primary phone (optional)" : `Phone ${index + 1} (optional)`}
                        value={phone}
                        onChange={(e) => handleQuickInviteePhoneChange(index, e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        aria-label="Remove phone"
                        onClick={() => handleRemoveQuickInviteePhone(index)}
                        disabled={quickInviteePhones.length === 1}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
                <TextField
                  size="small"
                  label="Notes (optional)"
                  value={quickNotes}
                  onChange={(e) => setQuickNotes(e.target.value)}
                  multiline
                  minRows={2}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel id="quick-recurrence-label">Repeat</InputLabel>
                    <Select
                      labelId="quick-recurrence-label"
                      value={quickRecurrence}
                      label="Repeat"
                      onChange={(e) => setQuickRecurrence(e.target.value)}
                    >
                      <MenuItem value="none">No recurrence</MenuItem>
                      <MenuItem value="day">Daily</MenuItem>
                      <MenuItem value="week">Weekly</MenuItem>
                      <MenuItem value="month">Monthly</MenuItem>
                      <MenuItem value="year">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="number"
                    label="Occurrences"
                    value={quickOccurrences}
                    onChange={(e) => setQuickOccurrences(e.target.value)}
                    inputProps={{ min: 1, max: 52 }}
                    disabled={quickRecurrence === "none"}
                    sx={{ width: { xs: "100%", sm: 140 } }}
                  />
                </Stack>
                <TextField select size="small" label="Remind me" value={quickReminder} onChange={e => setQuickReminder(e.target.value)} helperText="Email your signed-in account before each occurrence. Invitees will not receive this personal reminder.">
                  <MenuItem value="none">No reminder</MenuItem>
                  <MenuItem value="15">15 minutes before</MenuItem>
                  <MenuItem value="60">1 hour before</MenuItem>
                  <MenuItem value="1440">1 day before</MenuItem>
                </TextField>
                {quickRecurrence !== 'none' && <Typography variant="caption" color="text.secondary">Repeats using the {displayCalendar === 'ethiopic' ? 'Ethiopian' : 'Gregorian'} calendar in {displayTimezone.replaceAll('_', ' ')}. Each occurrence gets its own reminder when enabled.</Typography>}
                {quickError ? <Alert severity="error">{quickError}</Alert> : null}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    You can close this anytime with the top-right `X`.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleCreateQuickAppointment}
                    disabled={quickSaving}
                    sx={{ borderRadius: 2 }}
                  >
                    {quickSaving
                      ? "Saving..."
                      : quickRecurrence === "none"
                        ? "Create Quick Appointment"
                        : `Create ${quickOccurrences || 1} Quick Appointments`}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}

          {selectedBooking && selectedBooking !== "__quick__" && (
            <Box sx={sectionCardSx}>
              <Typography variant="subtitle1" sx={{ mb: 0.75, fontWeight: 700 }}>
                Booking Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Complete the booking details below to schedule this event on the calendar.
              </Typography>
              <AppointmentBooking
                selectedBookingData={selectedBooking}
                selectedBookingDay={selectedBookingDay}
                selectedBookingMonth={selectedBookingMonth}
                businessUrl={selectedBusiness?.businessBasic?.url}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Dialog */}
      <StyledDialog
        open={isUpdateDialogOpen}
        onClose={handleCloseUpdateDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperSx }}
      >
        {bookingData ? (
          Array.isArray(bookingData) ? (
            // Render grouped event update dialog
            <Box display="flex" flexDirection="column">
              <DialogTitle
                sx={{
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 1.75, sm: 2 },
                  borderBottom: "1px solid rgba(29, 78, 216, 0.10)",
                  bgcolor: "background.paper",
                  color: "text.primary",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ pr: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Manage Attendees for {bookingData[0].bookingType.title} at{" "}
                    {formatClockText(new Date(bookingData[0].startTime).toLocaleTimeString('en-US', {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true, timeZone: displayTimezone,
                    }), displayClock)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Review the event details below, notify attendees if anything changed, or cancel bookings when needed.
                  </Typography>
                  </Box>
                  {!groupedBookingsAreGoogleOnly ? (
                    <>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setGroupMenuAnchor(e.currentTarget);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={groupMenuAnchor}
                        open={Boolean(groupMenuAnchor)}
                        onClose={() => setGroupMenuAnchor(null)}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setGroupMenuAnchor(null);
                            handleNotifyGroup();
                          }}
                        >
                          Notify Group About Change
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setGroupMenuAnchor(null);
                            handleCancelGroup();
                          }}
                        >
                          Cancel Group Booking
                        </MenuItem>
                      </Menu>
                    </>
                  ) : null}
                </Box>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseUpdateDialog}
                  sx={{
                    position: "absolute",
                    right: 14,
                    top: 14,
                    color: "text.primary",
                    border: 1, borderColor: "divider",
                    backgroundColor: "background.paper",
                    "&:hover": { backgroundColor: "rgba(29,78,216,0.05)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
                <Stack spacing={2}>
                  {groupedBookingsAreGoogleOnly ? (
                    <Alert
                      severity="info"
                      action={
                        groupedGoogleLink ? (
                          <Link
                            href={groupedGoogleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            Open Google
                          </Link>
                        ) : null
                      }
                    >
                      Google calendar appointments are view-only here. To reschedule or cancel them,
                      use Google Calendar.
                    </Alert>
                  ) : null}
                  <Box sx={sectionCardSx}>
                    {renderScheduleContext(
                      bookingData[0]?.event,
                      bookingData[0]?.bookingType,
                      true
                    )}
                  </Box>
                  <Divider />
                  {bookingData.map((bd, index) => (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{
                        border: 1, borderColor: "divider",
                        borderRadius: 2,
                        p: 1.5,
                        backgroundColor: "background.paper",
                      }}
                    >
                      <Box>
                        <Typography variant="body1">
                          <strong>Name:</strong> {bd.event?.inviteeName || "N/A"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Email:</strong> {bd.event?.inviteeEmail || "N/A"}
                        </Typography>
                        {bd.event?.inviteePhoneNumbers && bd.event.inviteePhoneNumbers.length > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Phone:</strong> {bd.event.inviteePhoneNumbers.join(", ")}
                          </Typography>
                        )}
                      </Box>
                      {!isGoogleBooking(bd?.event || bd) ? (
                        <Stack direction="row" spacing={0.5}>
                          <IconButton edge="end" color="error" onClick={() => handleCancelIndividual(bd)}>
                            <CloseIcon />
                          </IconButton>
                          <IconButton edge="end" color="primary" onClick={() => handleNotifyIndividual(bd)}>
                            <CampaignIcon />
                          </IconButton>
                        </Stack>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </DialogContent> 
            </Box>
          ) : (
            // Render single booking update dialog (existing behavior)
            <Box display="flex" flexDirection="column">
              <DialogTitle
                sx={{
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 1.75, sm: 2 },
                  borderBottom: "1px solid rgba(29, 78, 216, 0.10)",
                  bgcolor: "background.paper",
                  color: "text.primary",
                }}
              >
                <Box sx={{ pr: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Manage Invitee
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Review this booking, confirm the invitee details, and make changes or send updates if needed.
                  </Typography>
                </Box>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseUpdateDialog}
                  sx={{
                    position: "absolute",
                    right: 14,
                    top: 14,
                    color: "text.primary",
                    border: 1, borderColor: "divider",
                    backgroundColor: "background.paper",
                    "&:hover": { backgroundColor: "rgba(29,78,216,0.05)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
                <Stack spacing={2}>
                  {singleBookingIsGoogle ? (
                    <Alert
                      severity="info"
                      action={
                        singleGoogleLink ? (
                          <Link
                            href={singleGoogleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            Open Google
                          </Link>
                        ) : null
                      }
                    >
                      This Google calendar appointment is view-only here. To reschedule or cancel it,
                      use Google Calendar.
                    </Alert>
                  ) : null}
                  <Box sx={sectionCardSx}>
                  <Typography variant="body1">
                    <strong>Name:</strong> {bookingData.event?.inviteeName || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {bookingData.inviteeEmail || "N/A"}
                  </Typography>
                  {bookingData.inviteePhoneNumbers && bookingData.inviteePhoneNumbers.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong>{" "}
                      {bookingData.inviteePhoneNumbers.map((phone, index) => (
                        <React.Fragment key={phone}>
                          <a href={`tel:${phone}`} style={{ textDecoration: "none", color: "inherit" }}>
                            {phone}
                          </a>
                          {index < bookingData.inviteePhoneNumbers.length - 1 ? ", " : ""}
                        </React.Fragment>
                        ))}
                    </Typography>
                  )}
                  {renderScheduleContext(
                    bookingData.event,
                    bookingData.bookingType,
                    true
                  )}
                  </Box>
                </Stack>
              </DialogContent>
              {!singleBookingIsGoogle ? (
                <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setSelectedBookings([bookingData]);
                      setShowNotifyModal(true);
                    }}
                    startIcon={<CampaignIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Notify About Change
                  </Button>
                  <Button variant="outlined" color="error" onClick={() => {
                    setSelectedBookings([bookingData]);
                    setShowCancelModal(true);
                  }} startIcon={<CloseIcon />} sx={{ borderRadius: 2 }}>
                    Cancel Booking
                  </Button>
                </DialogActions>
              ) : null}
            </Box>
          )
        ) : (
          <Typography>No bookings for this date.</Typography>
        )}
          </StyledDialog>
          {showCancelModal && (
              <CancelSchedule
              currentData={selectedBookings.map(b => (b.event ))}
              answersData={selectedBookings.map(b => (b.event ))}
              showCancelModal={showCancelModal}
              setShowCancelModal={cancelModal}
            />
          )}
          {showNotifyModal && (
            <NotifyScheduleChange
              currentData={selectedBookings.map((b) => b.event)}
              showNotifyModal={showNotifyModal}
              setShowNotifyModal={setShowNotifyModal}
            />
          )}
      </>
  );
}; 
export default BookItOrCreate;  
